/**
 * Configuration — Support Tickets
 * Spec: docs/specs/configuration-support-tickets.md (Status NOT YET READY — see §11).
 *
 * Creates, views, edits, validates, saves and stores Support Ticket Rules.
 * Each Ticket Type has exactly one Rule (§7). Permanent Rule ID is STK-####.
 * No Condition Fields are defined (§6/§11) — every Rule has No Conditions.
 * There is no Rule State, activation, pause or Delete control (§3).
 */

import { formatConfigListDate } from './tradeCategoryConfig';

export { formatConfigListDate };

export const SUPPORT_TICKET_CONFIG_ROUTE = '/admin/configuration/support-ticket';

export const SUPPORT_TICKET_MODULE = 'Support Tickets';

export const TICKET_TYPES = {
  GENERAL_ENQUIRY: 'General Enquiry',
  ACCOUNT_ISSUE: 'Account Issue',
  BILLING_SUBSCRIPTIONS: 'Billing & Subscriptions',
  TECHNICAL_ISSUE: 'Technical Issue',
  FEEDBACK_SUGGESTIONS: 'Feedback and Suggestions',
  OTHER: 'Other',
};

/** §4.1 — the six fixed Ticket Types. Appeal is not a Ticket Type. */
export const TICKET_TYPE_OPTIONS = [
  TICKET_TYPES.GENERAL_ENQUIRY,
  TICKET_TYPES.ACCOUNT_ISSUE,
  TICKET_TYPES.BILLING_SUBSCRIPTIONS,
  TICKET_TYPES.TECHNICAL_ISSUE,
  TICKET_TYPES.FEEDBACK_SUGGESTIONS,
  TICKET_TYPES.OTHER,
];

export const PRIORITY = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
};

export const PRIORITY_OPTIONS = [
  { value: PRIORITY.LOW, label: 'Low' },
  { value: PRIORITY.MEDIUM, label: 'Medium' },
  { value: PRIORITY.HIGH, label: 'High' },
];

export const AVAILABILITY = {
  ENABLED: 'Enabled',
  DISABLED: 'Disabled',
};

/** §5.1 — Help Centre / Contact Us is one choice covering both public entry points. */
export const ENTRY_POINT = {
  HELP_CENTRE_CONTACT_US: 'Help Centre / Contact Us',
  CUSTOMER_DASHBOARD: 'Customer Dashboard',
  BUSINESS_DASHBOARD: 'Business Dashboard',
};

/** Visitor Entry Point is fixed — Help Centre / Contact Us. */
export const VISITOR_ENTRY_POINT = ENTRY_POINT.HELP_CENTRE_CONTACT_US;

export const CUSTOMER_ENTRY_POINT_OPTIONS = [
  { value: ENTRY_POINT.HELP_CENTRE_CONTACT_US, label: 'Help Centre / Contact Us' },
  { value: ENTRY_POINT.CUSTOMER_DASHBOARD, label: 'Customer Dashboard' },
];

export const BUSINESS_ENTRY_POINT_OPTIONS = [
  { value: ENTRY_POINT.HELP_CENTRE_CONTACT_US, label: 'Help Centre / Contact Us' },
  { value: ENTRY_POINT.BUSINESS_DASHBOARD, label: 'Business Dashboard' },
];

/** §5.3 — read-only note, shown only for the General Enquiry Rule. */
export const COMPLIANCE_INFORMATION_REQUESTS_NOTE =
  "Uses this Rule’s Ticket Type and Automatic Priority.";

export const CONFIG_LIST_ACTION_LABELS = {
  view: 'View',
  edit: 'Edit',
};

export const MESSAGES = {
  saved: 'Support Ticket Rule saved successfully.',
  saveFailed: 'The Rule could not be saved. Your changes have been kept.',
  noChange: 'No changes to save.',
  empty: 'No Support Ticket Rules match your filters.',
  notFound: 'Support Ticket Rule could not be found.',
};

/** §9 — Validation Messages, exact wording. */
export const VALIDATION_MESSAGES = {
  ruleNameRequired: 'Enter a Rule Name.',
  ruleNameDuplicate: 'This Rule Name is already used.',
  descriptionRequired: 'Enter a Description.',
  selectField: (fieldName) => `Select ${fieldName}.`,
  incompatibleSelection: 'Select a compatible value.',
  ticketTypeTaken: (ruleId) =>
    `A Rule already exists for this Ticket Type: ${ruleId}. Edit the existing Rule.`,
  staleEdit: 'This Rule has changed. Check the latest saved Rule.',
  changeReasonRequired: 'Enter a Change Reason.',
  permissionDenied: 'You do not have permission to save this Rule.',
  requiredCheckIncomplete: 'Required checks could not be completed. Try again.',
  saveFailed: 'The Rule could not be saved. Your changes have been kept.',
};

export function formatSupportTicketConfigDate(value) {
  if (!value || !String(value).trim()) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return '—';
  }
}

export function getAvailabilityLabel(enabled) {
  return enabled ? AVAILABILITY.ENABLED : AVAILABILITY.DISABLED;
}

/** Screen 1 Available For — submitter types whose Availability is Enabled, or None. */
export function getAvailableForSummary(rule) {
  const parts = [];
  if (rule?.visitorAvailable) parts.push('Visitor');
  if (rule?.customerAvailable) parts.push('Customer');
  if (rule?.businessAvailable) parts.push('Business');
  return parts.length ? parts.join(', ') : 'None';
}

/** Screen 1 Entry Points — each entry point available to at least one enabled submitter type. */
export function getEntryPointsSummary(rule) {
  const points = [];
  if (rule?.visitorAvailable) points.push(VISITOR_ENTRY_POINT);
  if (rule?.customerAvailable) {
    (rule.customerEntryPoints || []).forEach((point) => {
      if (!points.includes(point)) points.push(point);
    });
  }
  if (rule?.businessAvailable) {
    (rule.businessEntryPoints || []).forEach((point) => {
      if (!points.includes(point)) points.push(point);
    });
  }
  return points.length ? points.join('; ') : 'Not Applicable';
}

/** §6/§11 — No Condition Fields are defined, so every Rule has No Conditions. */
export function getConditionsSummary(rule) {
  const conditions = Array.isArray(rule?.conditions) ? rule.conditions : [];
  if (!conditions.length) return 'No Conditions';
  return conditions
    .map((c) => [c.field, c.operator, (c.values || []).join(', ')].filter(Boolean).join(' · '))
    .join(' AND ');
}

/** Row actions — View · Edit only. No Delete, no activation/deactivation. */
export function getListActionsForConfiguration() {
  return ['view', 'edit'];
}

export function findSupportTicketConfiguration(configurations, configId) {
  const id = decodeURIComponent(String(configId || '')).trim();
  if (!id) return null;
  return (configurations || []).find((row) => row.id === id) || null;
}

function legacyEntryPointMap(value) {
  if (
    value === 'Help Centre' ||
    value === 'Contact Us' ||
    value === 'Help Centre / Contact Us'
  ) {
    return ENTRY_POINT.HELP_CENTRE_CONTACT_US;
  }
  return value;
}

function normalizeEntryPoints(values, allowed) {
  const allowedSet = new Set(allowed);
  const mapped = (values || []).map(legacyEntryPointMap);
  return [...new Set(mapped.filter((value) => allowedSet.has(value)))];
}

function hasDuplicateEntryPoints(values) {
  const mapped = (values || []).map(legacyEntryPointMap);
  return mapped.length !== new Set(mapped).size;
}

/** Ignoring case and extra spaces (§3). */
export function normalizeRuleNameForCompare(value) {
  return String(value || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

/** Permanent Rule ID — STK-####, assigned after the first successful Save (§3). */
export function nextSupportTicketRuleId(rules) {
  const nums = (rules || []).map((rule) => {
    const match = String(rule.id || '').match(/^STK-(\d+)$/i);
    return match ? parseInt(match[1], 10) : 0;
  });
  const max = nums.length ? Math.max(...nums) : 0;
  return `STK-${String(max + 1).padStart(4, '0')}`;
}

/** Screen 1 default sort — Rule ID, ascending (§8). */
export function sortSupportTicketConfigurationsById(rules) {
  return [...(rules || [])].sort((a, b) => {
    const aNum = parseInt(String(a.id || '').match(/\d+/)?.[0] || '0', 10);
    const bNum = parseInt(String(b.id || '').match(/\d+/)?.[0] || '0', 10);
    if (aNum !== bNum) return aNum - bNum;
    return String(a.id || '').localeCompare(String(b.id || ''));
  });
}

/**
 * Canonical Rule shape. `changeReason` is change metadata kept only on the
 * working draft during Add/Edit — it is not part of the saved Rule definition.
 */
export function normalizeSupportTicketConfiguration(config = {}) {
  const ticketType = config.ticketType || '';
  const customerAllowed = CUSTOMER_ENTRY_POINT_OPTIONS.map((o) => o.value);
  const businessAllowed = BUSINESS_ENTRY_POINT_OPTIONS.map((o) => o.value);
  const visitorAvailable = Boolean(config.visitorAvailable);
  const customerAvailable = Boolean(config.customerAvailable);
  const businessAvailable = Boolean(config.businessAvailable);
  return {
    id: config.id || '',
    module: SUPPORT_TICKET_MODULE,
    ruleName: config.ruleName || '',
    description: config.description || '',
    ticketType,
    visitorAvailable,
    customerAvailable,
    businessAvailable,
    visitorEntryPoint: VISITOR_ENTRY_POINT,
    // Entry Points are hidden and not saved when their submitter type is Disabled (§9).
    customerEntryPoints: customerAvailable
      ? normalizeEntryPoints(config.customerEntryPoints, customerAllowed)
      : [],
    businessEntryPoints: businessAvailable
      ? normalizeEntryPoints(config.businessEntryPoints, businessAllowed)
      : [],
    automaticPriority: config.automaticPriority || '',
    // No Condition Fields are defined for Support Ticket Rules (§6/§11).
    conditions: [],
    changeReason: config.changeReason || '',
    dateAdded: config.dateAdded || '',
    addedBy: config.addedBy || '',
    lastUpdated: config.lastUpdated || '',
    lastUpdatedBy: config.lastUpdatedBy || '',
  };
}

/**
 * Validate a proposed Rule (§9/§10).
 * Pass `isAdd` for Add, `baseline` (saved Rule) for Edit stale/scope checks.
 */
export function collectSupportTicketConfigValidationErrors(
  draft,
  {
    isAdd = false,
    baseline = null,
    allConfigurations = [],
    openedLastUpdated = null,
    canSave = true,
  } = {},
) {
  const errors = {};

  if (!canSave) {
    errors.form = VALIDATION_MESSAGES.permissionDenied;
    return errors;
  }

  if (!isAdd && !baseline) {
    errors.form = VALIDATION_MESSAGES.requiredCheckIncomplete;
    return errors;
  }

  const others = (allConfigurations || []).filter((row) => row.id !== draft?.id);

  const ruleName = String(draft?.ruleName || '').trim();
  if (!ruleName) {
    errors.ruleName = VALIDATION_MESSAGES.ruleNameRequired;
  } else {
    const normalized = normalizeRuleNameForCompare(ruleName);
    if (others.some((row) => normalizeRuleNameForCompare(row.ruleName) === normalized)) {
      errors.ruleName = VALIDATION_MESSAGES.ruleNameDuplicate;
    }
  }

  const description = String(draft?.description || '').trim();
  if (!description) {
    errors.description = VALIDATION_MESSAGES.descriptionRequired;
  }

  if (isAdd) {
    if (!draft?.ticketType) {
      errors.ticketType = VALIDATION_MESSAGES.selectField('Ticket Type');
    } else if (!TICKET_TYPE_OPTIONS.includes(draft.ticketType)) {
      errors.ticketType = VALIDATION_MESSAGES.incompatibleSelection;
    } else {
      const existing = others.find((row) => row.ticketType === draft.ticketType);
      if (existing) {
        errors.ticketType = VALIDATION_MESSAGES.ticketTypeTaken(existing.id);
      }
    }
  } else if (baseline && draft?.ticketType !== baseline.ticketType) {
    errors.form = VALIDATION_MESSAGES.requiredCheckIncomplete;
  }

  const customerAllowed = CUSTOMER_ENTRY_POINT_OPTIONS.map((o) => o.value);
  const businessAllowed = BUSINESS_ENTRY_POINT_OPTIONS.map((o) => o.value);
  const rawCustomer = (draft?.customerEntryPoints || []).map(legacyEntryPointMap);
  const rawBusiness = (draft?.businessEntryPoints || []).map(legacyEntryPointMap);
  const customerPoints = normalizeEntryPoints(draft?.customerEntryPoints, customerAllowed);
  const businessPoints = normalizeEntryPoints(draft?.businessEntryPoints, businessAllowed);

  if (hasDuplicateEntryPoints(draft?.customerEntryPoints)) {
    errors.customerEntryPoints = VALIDATION_MESSAGES.incompatibleSelection;
  } else if (rawCustomer.some((p) => !customerAllowed.includes(p))) {
    errors.customerEntryPoints = VALIDATION_MESSAGES.incompatibleSelection;
  } else if (draft?.customerAvailable && customerPoints.length === 0) {
    errors.customerEntryPoints = VALIDATION_MESSAGES.selectField('Customer Entry Points');
  }

  if (hasDuplicateEntryPoints(draft?.businessEntryPoints)) {
    errors.businessEntryPoints = VALIDATION_MESSAGES.incompatibleSelection;
  } else if (rawBusiness.some((p) => !businessAllowed.includes(p))) {
    errors.businessEntryPoints = VALIDATION_MESSAGES.incompatibleSelection;
  } else if (draft?.businessAvailable && businessPoints.length === 0) {
    errors.businessEntryPoints = VALIDATION_MESSAGES.selectField('Business Entry Points');
  }

  if (!draft?.automaticPriority) {
    errors.automaticPriority = VALIDATION_MESSAGES.selectField('Automatic Priority');
  } else if (!PRIORITY_OPTIONS.some((o) => o.value === draft.automaticPriority)) {
    errors.automaticPriority = VALIDATION_MESSAGES.incompatibleSelection;
  }

  if (Array.isArray(draft?.conditions) && draft.conditions.length > 0) {
    errors.conditions = VALIDATION_MESSAGES.incompatibleSelection;
  }

  if (!isAdd && !String(draft?.changeReason || '').trim()) {
    errors.changeReason = VALIDATION_MESSAGES.changeReasonRequired;
  }

  if (
    !isAdd &&
    baseline &&
    openedLastUpdated &&
    baseline.lastUpdated &&
    String(openedLastUpdated) !== String(baseline.lastUpdated)
  ) {
    errors.stale = VALIDATION_MESSAGES.staleEdit;
  }

  return errors;
}

export function supportTicketConfigHasChanges(before, after) {
  if (!before || !after) return true;
  if ((before.ruleName || '').trim() !== (after.ruleName || '').trim()) return true;
  if ((before.description || '').trim() !== (after.description || '').trim()) return true;
  if (Boolean(before.visitorAvailable) !== Boolean(after.visitorAvailable)) return true;
  if (Boolean(before.customerAvailable) !== Boolean(after.customerAvailable)) return true;
  if (Boolean(before.businessAvailable) !== Boolean(after.businessAvailable)) return true;
  if (before.automaticPriority !== after.automaticPriority) return true;
  if (
    (before.customerEntryPoints || []).join('|') !== (after.customerEntryPoints || []).join('|')
  ) {
    return true;
  }
  if (
    (before.businessEntryPoints || []).join('|') !== (after.businessEntryPoints || []).join('|')
  ) {
    return true;
  }
  return false;
}

function buildSeedRule({
  id,
  ruleName,
  description,
  ticketType,
  visitorAvailable,
  customerAvailable,
  businessAvailable,
  customerEntryPoints,
  businessEntryPoints,
  automaticPriority,
  dateAdded,
  lastUpdated,
  addedBy,
  lastUpdatedBy,
}) {
  return normalizeSupportTicketConfiguration({
    id,
    ruleName,
    description,
    ticketType,
    visitorAvailable,
    customerAvailable,
    businessAvailable,
    customerEntryPoints,
    businessEntryPoints,
    automaticPriority,
    dateAdded,
    lastUpdated,
    addedBy,
    lastUpdatedBy,
  });
}

/**
 * Platform setup creates one Rule per Ticket Type (§3/§4.1). Rule Names,
 * Descriptions and settings shown here are the spec's illustrative Screen 1
 * examples — they are not production values (§8, §11).
 */
export function buildSampleSupportTicketConfigurations() {
  const addedBy = 'System';

  return [
    buildSeedRule({
      id: 'STK-0001',
      ruleName: 'General Enquiry Tickets',
      description: 'Handles general questions submitted by Visitors, Customers and Businesses.',
      ticketType: TICKET_TYPES.GENERAL_ENQUIRY,
      visitorAvailable: true,
      customerAvailable: true,
      businessAvailable: true,
      customerEntryPoints: [ENTRY_POINT.HELP_CENTRE_CONTACT_US, ENTRY_POINT.CUSTOMER_DASHBOARD],
      businessEntryPoints: [ENTRY_POINT.HELP_CENTRE_CONTACT_US, ENTRY_POINT.BUSINESS_DASHBOARD],
      automaticPriority: PRIORITY.LOW,
      dateAdded: '2026-08-01T00:00:00.000Z',
      lastUpdated: '2026-08-01T00:00:00.000Z',
      addedBy,
      lastUpdatedBy: addedBy,
    }),
    buildSeedRule({
      id: 'STK-0002',
      ruleName: 'Account Issue Tickets',
      description: 'Handles reports of account access or account-related problems.',
      ticketType: TICKET_TYPES.ACCOUNT_ISSUE,
      visitorAvailable: true,
      customerAvailable: true,
      businessAvailable: true,
      customerEntryPoints: [ENTRY_POINT.HELP_CENTRE_CONTACT_US, ENTRY_POINT.CUSTOMER_DASHBOARD],
      businessEntryPoints: [ENTRY_POINT.HELP_CENTRE_CONTACT_US, ENTRY_POINT.BUSINESS_DASHBOARD],
      automaticPriority: PRIORITY.MEDIUM,
      dateAdded: '2026-08-02T00:00:00.000Z',
      lastUpdated: '2026-08-02T00:00:00.000Z',
      addedBy,
      lastUpdatedBy: addedBy,
    }),
    buildSeedRule({
      id: 'STK-0003',
      ruleName: 'Billing Tickets',
      description: 'Handles billing and subscription questions from Customers and Businesses.',
      ticketType: TICKET_TYPES.BILLING_SUBSCRIPTIONS,
      visitorAvailable: false,
      customerAvailable: true,
      businessAvailable: true,
      customerEntryPoints: [ENTRY_POINT.HELP_CENTRE_CONTACT_US, ENTRY_POINT.CUSTOMER_DASHBOARD],
      businessEntryPoints: [ENTRY_POINT.HELP_CENTRE_CONTACT_US, ENTRY_POINT.BUSINESS_DASHBOARD],
      automaticPriority: PRIORITY.MEDIUM,
      dateAdded: '2026-08-03T00:00:00.000Z',
      lastUpdated: '2026-08-03T00:00:00.000Z',
      addedBy,
      lastUpdatedBy: addedBy,
    }),
    buildSeedRule({
      id: 'STK-0004',
      ruleName: 'Technical Issue Tickets',
      description: 'Handles reports of technical problems with the platform.',
      ticketType: TICKET_TYPES.TECHNICAL_ISSUE,
      visitorAvailable: true,
      customerAvailable: true,
      businessAvailable: true,
      customerEntryPoints: [ENTRY_POINT.HELP_CENTRE_CONTACT_US, ENTRY_POINT.CUSTOMER_DASHBOARD],
      businessEntryPoints: [ENTRY_POINT.HELP_CENTRE_CONTACT_US, ENTRY_POINT.BUSINESS_DASHBOARD],
      automaticPriority: PRIORITY.HIGH,
      dateAdded: '2026-08-04T00:00:00.000Z',
      lastUpdated: '2026-08-04T00:00:00.000Z',
      addedBy,
      lastUpdatedBy: addedBy,
    }),
    buildSeedRule({
      id: 'STK-0005',
      ruleName: 'Feedback Tickets',
      description: 'Collects feedback and suggestions. Not currently offered to users.',
      ticketType: TICKET_TYPES.FEEDBACK_SUGGESTIONS,
      visitorAvailable: false,
      customerAvailable: false,
      businessAvailable: false,
      customerEntryPoints: [],
      businessEntryPoints: [],
      automaticPriority: PRIORITY.LOW,
      dateAdded: '2026-08-05T00:00:00.000Z',
      lastUpdated: '2026-08-05T00:00:00.000Z',
      addedBy,
      lastUpdatedBy: addedBy,
    }),
    buildSeedRule({
      id: 'STK-0006',
      ruleName: 'Other Tickets',
      description: 'Handles tickets that do not fit another Ticket Type.',
      ticketType: TICKET_TYPES.OTHER,
      visitorAvailable: false,
      customerAvailable: true,
      businessAvailable: true,
      customerEntryPoints: [ENTRY_POINT.HELP_CENTRE_CONTACT_US, ENTRY_POINT.CUSTOMER_DASHBOARD],
      businessEntryPoints: [ENTRY_POINT.HELP_CENTRE_CONTACT_US, ENTRY_POINT.BUSINESS_DASHBOARD],
      automaticPriority: PRIORITY.MEDIUM,
      dateAdded: '2026-08-06T00:00:00.000Z',
      lastUpdated: '2026-08-06T00:00:00.000Z',
      addedBy,
      lastUpdatedBy: addedBy,
    }),
  ];
}

/** Ticket Types in §4.1 that have no Rule yet — Add offers only these (§7). */
export function getAvailableTicketTypesForAdd(allConfigurations) {
  const taken = new Set((allConfigurations || []).map((rule) => rule.ticketType));
  return TICKET_TYPE_OPTIONS.filter((type) => !taken.has(type));
}
